/**
 * Phase 2.5 — candidate API clients.
 *
 * Typed wrappers for the three new endpoints:
 *   PATCH /api/t/{slug}/candidates/{id}/tenant-status
 *   POST  /api/t/{slug}/candidates/{id}/jd-pairings
 *   DELETE /api/t/{slug}/candidates/{id}/jd-pairings/{jd_id}
 *   GET   /api/t/{slug}/candidates/{id}/jd-pairings
 *
 * Every mutating request sends a client-generated Idempotency-Key header
 * (UUID v4).  GET requests do not.
 *
 * Idempotency-Key is generated fresh per logical request (not per retry)
 * via the shared `generateRequestId()` which already returns a UUID v4.
 */

import { apiClient } from "@/lib/apiClient";
import { generateRequestId } from "@/lib/requestId";

// ── Shared types ─────────────────────────────────────────────────────────────

/** Tenant-level status enum (Decision 5 / global-patterns §4.1). */
export type TenantStatus =
  | "available"
  | "hired"
  | "rejected"
  | "opted_out"
  | "unavailable";

/** Terminal outcomes for cascade semantics. */
export type TerminalTenantStatus = Extract<
  TenantStatus,
  "hired" | "rejected" | "opted_out"
>;

/** Cascade mode — keep_active is the default / safe path. */
export type CascadeMode = "keep_active" | "apply_to_all";

// ── Response shapes ───────────────────────────────────────────────────────────

interface PairingMutated {
  jd_id: string;
  jd_title: string;
  prior_stage: string | null;
  new_outcome: string;
}

interface CascadeSummary {
  jd_pairings_mutated: PairingMutated[];
  failures: [];
}

/** Shape of candidates.tenant_status patch response. */
export interface TenantStatusPatchResponse {
  candidate: {
    id: string;
    tenant_status: TenantStatus;
  };
  cascade_summary: CascadeSummary;
}

/** A single JD pairing row. */
export interface JdPairingRow {
  id?: string;
  tenant_id: string;
  candidate_id: string;
  jd_id: string;
  stage_id: string;
  terminal_outcome: string | null;
  created_at: string;
  updated_at: string;
  /** Present only on POST /jd-pairings responses. Null on idempotent replay. */
  run_id?: string | null;
}

// ── Endpoints ─────────────────────────────────────────────────────────────────

/**
 * PATCH /api/t/{slug}/candidates/{id}/tenant-status
 *
 * Changes the candidate's tenant-level status.  When `cascade='apply_to_all'`
 * and the new status is terminal, all other in-flight pairings are resolved
 * in a single atomic transaction.
 */
export async function patchTenantStatus(
  tenantSlug: string,
  candidateId: string,
  tenantStatus: TenantStatus,
  cascade: CascadeMode = "keep_active",
): Promise<TenantStatusPatchResponse> {
  const r = await apiClient.patch<TenantStatusPatchResponse>(
    `/api/t/${tenantSlug}/candidates/${candidateId}/tenant-status`,
    { tenant_status: tenantStatus, cascade },
    { headers: { "Idempotency-Key": generateRequestId() } },
  );
  return r.data;
}

/**
 * POST /api/t/{slug}/candidates/{id}/jd-pairings
 *
 * Adds the candidate to another JD pipeline at the `active` stage
 * (no-eval — recruiter can trigger rank-one explicitly from the new JD).
 */
export async function postJdPairing(
  tenantSlug: string,
  candidateId: string,
  jdId: string,
): Promise<JdPairingRow> {
  const r = await apiClient.post<JdPairingRow>(
    `/api/t/${tenantSlug}/candidates/${candidateId}/jd-pairings`,
    { jd_id: jdId, mode: "no_eval" },
    { headers: { "Idempotency-Key": generateRequestId() } },
  );
  return r.data;
}

/**
 * GET /api/t/{slug}/candidates/{id}/jd-pairings
 *
 * Backend ships ``PairingListResponse`` per
 * ``backend/app/schemas/jd_pairings.py`` — wire payload is
 * ``{"items": [PairingOut, ...]}``. Unwrap to an array here so every
 * consumer (`useCandidatePairings`, ``CandidatePage``, ``CandidatePageHeader``)
 * gets ``JdPairingRow[]`` directly and can call ``.map`` / ``.filter`` /
 * ``.length`` without defensive shape checks.
 *
 * Backend invariant pinned by ``backend/tests/integration/test_jd_pairings_contract.py``
 * (BUNDLE-FIXIT-1 Bucket D, invariant 13).
 */
export async function getJdPairings(
  tenantSlug: string,
  candidateId: string,
): Promise<JdPairingRow[]> {
  const r = await apiClient.get<{ items: JdPairingRow[] }>(
    `/api/t/${tenantSlug}/candidates/${candidateId}/jd-pairings`,
  );
  return r.data.items;
}

// ── P1-stage-counts: per-pair stage-move ────────────────────────────────────

/** Response shape for the per-pair stage-move endpoint. */
export interface PairingStageMoveResponse {
  pairing: JdPairingRow;
  prior_stage_id: string;
  prior_terminal_outcome: string | null;
}

/**
 * POST /api/t/{slug}/jds/{jdId}/pairings/{candidateId}/stage
 *
 * Move a single (candidate, jd) pairing to a new stage. P1-stage-counts
 * decision (b). Idempotent via per-call ``Idempotency-Key`` header.
 *
 * Same-stage targets are no-ops on the server (HTTP 200, body shape
 * identical to the transition path) — invariant #13.
 *
 * NOTE: this client method lands in this PR but is currently UNWIRED —
 * the move-pairing UI affordance (kebab → "Move to stage") ships in a
 * follow-up batch (``P1-stage-move-ui``) per cofounder Q1 ruling.
 */
export async function movePairingToStage(
  tenantSlug: string,
  jdId: string,
  candidateId: string,
  targetStageId: string,
): Promise<PairingStageMoveResponse> {
  const r = await apiClient.post<PairingStageMoveResponse>(
    `/api/t/${tenantSlug}/jds/${jdId}/pairings/${candidateId}/stage`,
    { target_stage_id: targetStageId },
    { headers: { "Idempotency-Key": generateRequestId() } },
  );
  return r.data;
}
